import { memo, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, CartesianGrid } from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  ChartConfig,
} from '@parrot-software/design-system';
import type { TransformedChartData } from '@reports/presentation/utils/chartDataTransformers';
import { formatAxisValue } from '@reports/presentation/utils/dataFormatters';
import { ReportDataFormats } from '@reports/domain/types';
import type { ReportDataConfig } from '@reports/domain/types';

export interface ReportLineChartProps {
  chartData: TransformedChartData[];
  lineColor: string;
  showTooltip: boolean;
  showLegend: boolean;
  tooltipFormatter: (value: any, name: string | number, item: any) => React.ReactElement;
  chartConfig: ChartConfig;
  format?: ReportDataConfig;
}

/**
 * ReportLineChart - Line chart renderer for reports
 *
 * Renders a line chart using Recharts with optional tooltips and data point dots.
 * Automatically adds currency symbol ($) to YAxis when format type is CURRENCY.
 * Memoized to prevent unnecessary re-renders.
 *
 * @param {ReportLineChartProps} props - Component properties
 * @param {TransformedChartData[]} props.chartData - Chart data in Recharts format
 * @param {string} props.lineColor - Line stroke color
 * @param {boolean} props.showTooltip - Whether to show tooltips on hover
 * @param {boolean} props.showLegend - Whether to show chart legend
 * @param {Function} props.tooltipFormatter - Function to format tooltip content
 * @param {ChartConfig} props.chartConfig - Chart configuration
 * @param {ReportDataConfig} [props.format] - Data format configuration for axis formatting
 * @returns {React.ReactElement} Line chart component
 *
 * @example
 * <ReportLineChart
 *   chartData={transformedData}
 *   lineColor="#EB6668"
 *   showTooltip={true}
 *   showLegend={true}
 *   tooltipFormatter={lineTooltipFormatter}
 *   chartConfig={{ value: { label: "Total Sales MXN", color: "#000000" } }}
 *   format={{ type: ReportDataFormats.CURRENCY }}
 * />
 */
const ReportLineChartComponent = ({
  chartData,
  lineColor,
  showTooltip,
  showLegend,
  tooltipFormatter,
  chartConfig,
  format,
}: ReportLineChartProps) => {
  // Create YAxis formatter that adds "$" prefix for currency format
  const yAxisFormatter = useMemo(() => {
    const isCurrency = format?.type === ReportDataFormats.CURRENCY;

    return (value: number): string => {
      const formattedValue = formatAxisValue(value);
      return isCurrency && formattedValue ? `$${formattedValue}` : formattedValue;
    };
  }, [format]);

  return (
    <ChartContainer className="h-[275px] w-full" config={chartConfig}>
      <ResponsiveContainer height="100%" width="100%">
        <LineChart data={chartData} margin={{ top: 20, left: 4, right: 4 }}>
          <CartesianGrid vertical={false} />
          <XAxis axisLine={false} dataKey="name" interval="preserveStartEnd" minTickGap={16} tickLine={false} />
          <YAxis axisLine={false} tickFormatter={yAxisFormatter} tickLine={false} />

          {showTooltip && (
            <ChartTooltip
              content={
                <ChartTooltipContent formatter={tooltipFormatter} labelClassName="font-normal text-muted-foreground" />
              }
            />
          )}
          <Line dataKey="value" dot={false} stroke={lineColor} strokeWidth={1} type="linear" />
          {showLegend && <ChartLegend content={<ChartLegendContent nameKey="value" />} verticalAlign="bottom" />}
        </LineChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
};

/**
 * Memoized version of ReportLineChart
 *
 * Prevents re-renders when props haven't changed, improving performance.
 */
export const ReportLineChart = memo(ReportLineChartComponent);
